/**
 * Settings → Voice agents (SPEC-VOX-301/302).
 *
 * Which speech-to-text and text-to-speech provider the AI voice agent uses on
 * this dealership's calls. Provider choice + credentials are stored per
 * tenant (encrypted at rest); the voice loop picks up changes on its next
 * call (≤60 s settings cache), no restart needed.
 *
 * Secret lifecycle (contract with the backend PUT):
 *   apiKey / option value null or omitted  → keep the stored secret
 *   apiKey / option value ""               → DELETE the stored secret
 *   apiKey / option value "sk-..."         → replace it
 * The backend merges options per key, so clearing one secret (e.g. an API
 * key) never touches another (e.g. a stored service-account JSON). Each
 * secret field shows a "Clear" button when something is stored for it.
 * Switching provider clears all of that side's stored secrets on save.
 */
import { useCallback, useEffect, useState } from 'react';
import { formatDateTime } from '../../lib/datetime';
import { voiceProvidersService, voiceAgentsService } from '../../lib/services';
import VoiceAgentsPanel from './VoiceAgentsPanel';

/* ────────────────────────────── shared bits ────────────────────────────── */

const input = {
  width: '100%',
  padding: '7px 10px',
  fontSize: 12,
  fontFamily: 'var(--font-mono)',
  color: 'var(--text-primary)',
  background: 'var(--bg-glass)',
  border: '1px solid var(--border-color)',
  borderRadius: 'var(--radius-sm)',
  outline: 'none',
};

const label = { fontSize: 11, fontWeight: 500, color: 'var(--text-secondary)', marginBottom: 4, display: 'block' };

const clearBtn = {
  fontSize: 10,
  padding: '2px 8px',
  border: '1px solid var(--border-color)',
  borderRadius: 'var(--radius-sm)',
  background: 'transparent',
  color: 'var(--danger)',
  cursor: 'pointer',
};

function Field({ title, hint, children, footer }) {
  return (
    <div style={{ marginBottom: 12 }}>
      <label style={label}>{title}</label>
      {children}
      {footer}
      {hint && <div className="mono text-xs text-muted" style={{ marginTop: 4 }}>{hint}</div>}
    </div>
  );
}

function StatusPill({ status }) {
  const map = {
    ACTIVE:         ['Verified',      'success'],
    PENDING:        ['Not verified',  'warning'],
    INVALID:        ['Rejected',      'danger'],
    DISABLED:       ['Disabled',      ''],
    NOT_CONFIGURED: ['Not configured', ''],
  };
  const [text, color] = map[status] || [status || '—', ''];
  return <span className={`pill ${color} filled`}>{text}</span>;
}

/* ─────────────────────────── provider catalogue ─────────────────────────── */
/* Field lists drive the form: which inputs each provider needs, with the
   defaults the voice loop assumes when a field is left blank. */

const STT_PROVIDERS = {
  deepgram: {
    label: 'Deepgram',
    blurb: 'Streaming Nova models. Lowest-latency default; native µ-law 8 kHz.',
    fields: [
      { key: 'apiKey',  title: 'API key', secret: true, hint: 'Deepgram console → API Keys' },
      { key: 'model',   title: 'Model',   placeholder: 'nova-2' },
      { key: 'language', title: 'Language', placeholder: 'en-AU' },
      { key: 'endpointingMs', title: 'Endpointing (ms)', placeholder: '800',
        hint: 'Silence that ends a caller’s turn. Higher = fewer mid-sentence cut-offs but slower replies; lower = snappier. For finer control set utterance_end_ms under Advanced.' },
    ],
  },
  azure: {
    label: 'Azure AI Speech',
    blurb: 'Native µ-law 8 kHz streaming; enterprise SLA.',
    fields: [
      { key: 'apiKey', title: 'Subscription key', secret: true, hint: 'Azure portal → Speech resource → Keys and Endpoint' },
      { key: 'region', title: 'Region', placeholder: 'australiaeast' },
      { key: 'language', title: 'Language', placeholder: 'en-AU' },
      { key: 'endpointingMs', title: 'Endpointing (ms)', placeholder: '800',
        hint: 'Silence that ends a caller’s turn. Higher = fewer mid-sentence cut-offs.' },
    ],
  },
  google: {
    label: 'Google Speech-to-Text v2',
    blurb: 'Streaming recognition, MULAW 8 kHz native, strong multilingual.',
    fields: [
      { key: 'apiKey', title: 'API key', secret: true,
        hint: 'APIs & Services → Credentials → API key (Speech-to-Text API enabled). Requires Project ID below. Or clear this and use a service-account JSON instead.' },
      { key: 'options.project_id', title: 'Project ID', placeholder: 'my-gcp-project',
        hint: 'Required with an API key; read from the JSON when using a service account.' },
      { key: 'options.credentials_json', title: 'Service-account JSON (alternative)', secret: true, textarea: true,
        hint: 'IAM → Service Accounts → key (JSON). Needs roles/speech.client.' },
      { key: 'model', title: 'Model', placeholder: 'latest_short' },
      { key: 'language', title: 'Language', placeholder: 'en-AU' },
      { key: 'endpointingMs', title: 'Endpointing (ms)', placeholder: '800',
        hint: 'Silence that ends a caller’s turn. Higher = fewer mid-sentence cut-offs.' },
    ],
  },
  openai: {
    label: 'OpenAI Realtime',
    blurb: 'Realtime transcription over WebSocket; g711_ulaw native.',
    fields: [
      { key: 'apiKey', title: 'API key', secret: true, hint: 'platform.openai.com → API keys' },
      { key: 'model',  title: 'Model', placeholder: 'gpt-4o-mini-transcribe' },
      { key: 'endpointingMs', title: 'Endpointing (ms)', placeholder: '800',
        hint: 'Silence that ends a caller’s turn. Higher = fewer mid-sentence cut-offs.' },
    ],
  },
};

const TTS_PROVIDERS = {
  elevenlabs: {
    label: 'ElevenLabs',
    blurb: 'True token-streaming voices; lowest first-byte latency.',
    fields: [
      { key: 'apiKey',  title: 'API key', secret: true, hint: 'elevenlabs.io → Profile → API key' },
      { key: 'voiceId', title: 'Voice ID', placeholder: 'e.g. pre-built AU voice' },
      { key: 'model',   title: 'Model', placeholder: 'eleven_turbo_v2' },
    ],
  },
  deepgram: {
    label: 'Deepgram Aura',
    blurb: 'Native µ-law 8 kHz — zero transcoding. Pair with Deepgram STT for the lowest end-to-end latency (one vendor, one key).',
    fields: [
      { key: 'apiKey',  title: 'API key', secret: true,
        hint: 'The SAME Deepgram key as STT — one key covers both. Deepgram console → API Keys.' },
      { key: 'voiceId', title: 'Voice (model id)', placeholder: 'aura-2-thalia-en',
        hint: 'Deepgram picks the voice via the model id, e.g. aura-2-thalia-en, aura-2-andromeda-en.' },
      { key: 'model',   title: 'Model (optional)', placeholder: 'leave blank — set the voice above' },
    ],
  },
  polly: {
    label: 'AWS Polly Neural',
    blurb: 'The ADR-003 fallback voice. Uses the pod IAM role when keys are blank.',
    fields: [
      { key: 'region',  title: 'AWS region', placeholder: 'ap-southeast-2' },
      { key: 'voiceId', title: 'Voice', placeholder: 'Olivia (en-AU neural)' },
      { key: 'options.aws_access_key_id',     title: 'Access key ID (optional)', secret: true },
      { key: 'options.aws_secret_access_key', title: 'Secret access key (optional)', secret: true,
        hint: 'Leave both blank to use the voice-loop pod IAM role (recommended in prod).' },
    ],
  },
  azure: {
    label: 'Azure Neural TTS',
    blurb: 'Emits raw 8 kHz µ-law natively — zero transcoding.',
    fields: [
      { key: 'apiKey',  title: 'Subscription key', secret: true, hint: 'Same Speech resource as Azure STT.' },
      { key: 'region',  title: 'Region', placeholder: 'australiaeast' },
      { key: 'voiceId', title: 'Voice', placeholder: 'en-AU-NatashaNeural' },
    ],
  },
  google: {
    label: 'Google Cloud TTS',
    blurb: 'WaveNet / Neural2 voices, MULAW 8 kHz output.',
    fields: [
      { key: 'apiKey', title: 'API key', secret: true,
        hint: 'Simplest auth for TTS. Or clear this and paste service-account JSON below instead.' },
      { key: 'options.credentials_json', title: 'Service-account JSON (alternative)', secret: true, textarea: true },
      { key: 'voiceId', title: 'Voice', placeholder: 'en-AU-Neural2-A' },
    ],
  },
  openai: {
    label: 'OpenAI TTS',
    blurb: 'gpt-4o-mini-tts voices; resampled to 8 kHz by the voice loop.',
    fields: [
      { key: 'apiKey',  title: 'API key', secret: true },
      { key: 'model',   title: 'Model', placeholder: 'gpt-4o-mini-tts' },
      { key: 'voiceId', title: 'Voice', placeholder: 'alloy' },
    ],
  },
};

/* ─────────────────────────────── form state ─────────────────────────────── */

const emptySide = (provider) => ({
  provider, model: '', voiceId: '', language: '', region: '',
  endpointingMs: null, apiKey: null, options: {},
  advancedJson: '',     // free-form provider-specific options (JSON object)
  clears: [],           // field keys marked "delete stored secret on save"
});

/** Parse the Advanced-options JSON into a flat {key: "value"} string map.
 *  Returns { options, error }. Empty/whitespace → no options, no error. */
function parseAdvanced(text) {
  const raw = (text || '').trim();
  if (!raw) return { options: {}, error: null };
  let obj;
  try {
    obj = JSON.parse(raw);
  } catch {
    return { options: {}, error: 'Advanced options must be valid JSON, e.g. {"utterance_end_ms": "1600"}' };
  }
  if (obj === null || typeof obj !== 'object' || Array.isArray(obj)) {
    return { options: {}, error: 'Advanced options must be a JSON object of key/value pairs.' };
  }
  const options = {};
  for (const [k, v] of Object.entries(obj)) {
    if (v === null || v === undefined) continue;
    options[k] = typeof v === 'string' ? v : String(v);   // provider options are strings
  }
  return { options, error: null };
}

function getField(side, key) {
  if (key.startsWith('options.')) return side.options?.[key.slice(8)] ?? '';
  return side[key] ?? '';
}

function setField(side, key, value) {
  // Typing into a field cancels a pending clear for it — the typed value wins.
  const clears = value ? (side.clears || []).filter((k) => k !== key) : (side.clears || []);
  if (key.startsWith('options.')) {
    return { ...side, clears, options: { ...(side.options || {}), [key.slice(8)]: value } };
  }
  return { ...side, clears, [key]: value };
}

/** Is there a stored secret on the server for this field? */
function storedFor(savedSide, fieldKey) {
  if (!savedSide) return false;
  if (fieldKey === 'apiKey') return !!savedSide.apiKeyMasked;
  if (fieldKey.startsWith('options.')) {
    return (savedSide.storedOptionKeys || []).includes(fieldKey.slice(8));
  }
  return false;
}

function ProviderForm({ kind, catalogue, side, setSide, saved }) {
  const meta = catalogue[side.provider] || Object.values(catalogue)[0];
  const savedSide = saved?.[kind];
  // Clearing / stored indicators only make sense while staying on the saved
  // provider — a provider switch wipes that side's secrets server-side anyway.
  const sameProvider = savedSide?.provider === side.provider;

  const markClear = (key) => setSide({
    ...setField(side, key, ''),                     // blank any typed value
    clears: [...new Set([...(side.clears || []), key])],
  });
  const undoClear = (key) => setSide({
    ...side, clears: (side.clears || []).filter((k) => k !== key),
  });

  return (
    <div className="card" style={{ padding: 16, flex: 1, minWidth: 320 }}>
      <div className="flex-between" style={{ marginBottom: 10 }}>
        <div className="card-label">{kind === 'stt' ? 'SPEECH-TO-TEXT' : 'TEXT-TO-SPEECH'}</div>
        {savedSide?.hasCredentials && sameProvider && (
          <span className="mono text-xs text-success">credentials stored{savedSide.apiKeyMasked ? ` · ${savedSide.apiKeyMasked}` : ''}</span>
        )}
      </div>

      <Field title="Provider">
        <select
          style={input}
          value={side.provider}
          onChange={(e) => setSide({ ...emptySide(e.target.value) })}
        >
          {Object.entries(catalogue).map(([id, p]) => (
            <option key={id} value={id}>{p.label}</option>
          ))}
        </select>
      </Field>
      {savedSide && !sameProvider && (
        <div className="mono text-xs text-warning" style={{ marginBottom: 10 }}>
          Switching provider — the stored {savedSide.provider} credentials for this side will be removed on save.
        </div>
      )}
      <div className="mono text-xs text-muted" style={{ marginBottom: 12 }}>{meta.blurb}</div>

      {meta.fields.map((f) => {
        const isStored = sameProvider && f.secret && storedFor(savedSide, f.key);
        const marked = (side.clears || []).includes(f.key);
        const footer = f.secret && sameProvider ? (
          marked ? (
            <div className="mono text-xs text-danger" style={{ marginTop: 4, display: 'flex', gap: 8, alignItems: 'center' }}>
              <span>stored value will be removed on save</span>
              <button type="button" style={{ ...clearBtn, color: 'var(--text-secondary)' }} onClick={() => undoClear(f.key)}>Undo</button>
            </div>
          ) : isStored ? (
            <div className="mono text-xs text-muted" style={{ marginTop: 4, display: 'flex', gap: 8, alignItems: 'center' }}>
              <span>stored{f.key === 'apiKey' && savedSide.apiKeyMasked ? `: ${savedSide.apiKeyMasked}` : ' ✓'}</span>
              <button type="button" style={clearBtn} onClick={() => markClear(f.key)}>Clear</button>
            </div>
          ) : null
        ) : null;

        return (
          <Field key={f.key} title={f.title} hint={f.hint} footer={footer}>
            {f.textarea ? (
              <textarea
                style={{ ...input, minHeight: 72, resize: 'vertical' }}
                placeholder={isStored && !marked ? '•••••••• (stored — paste to replace, or Clear below)' : (f.placeholder || '')}
                value={getField(side, f.key) ?? ''}
                onChange={(e) => setSide(setField(side, f.key, e.target.value))}
              />
            ) : (
              <input
                style={input}
                type={f.secret ? 'password' : 'text'}
                placeholder={isStored && !marked ? '•••••••• (stored — type to replace, or Clear below)' : (f.placeholder || '')}
                value={getField(side, f.key) ?? ''}
                onChange={(e) => setSide(setField(side, f.key, e.target.value))}
                autoComplete="off"
              />
            )}
          </Field>
        );
      })}

      {(() => {
        const adv = parseAdvanced(side.advancedJson);
        const storedKeys = (savedSide?.storedOptionKeys || [])
          .filter((k) => !['credentials_json', 'aws_access_key_id', 'aws_secret_access_key', 'project_id'].includes(k));
        return (
          <Field
            title="Advanced options"
            hint={
              'Provider-specific extras as a JSON object of string values — passed straight through to '
              + (kind === 'stt' ? 'the STT' : 'the TTS') + ' client. '
              + (kind === 'stt' && side.provider === 'deepgram'
                  ? 'e.g. {"utterance_end_ms": "1600"} to let callers pause longer mid-sentence without being '
                    + 'cut off, or {"keywords": "HiLux, Ranger, Navara"} to teach the transcriber this '
                    + 'dealership’s model names (common terms like ute, SUV, test drive, rego are built in). '
                  : '')
              + 'Stored securely and not shown after saving — re-enter to change.'
            }
            footer={
              <>
                {adv.error && (
                  <div className="mono text-xs text-danger" style={{ marginTop: 4 }}>{adv.error}</div>
                )}
                {storedKeys.length > 0 && (
                  <div className="mono text-xs text-muted" style={{ marginTop: 4 }}>
                    currently set: {storedKeys.join(', ')}
                  </div>
                )}
              </>
            }
          >
            <textarea
              style={{ ...input, minHeight: 60, resize: 'vertical' }}
              placeholder={kind === 'stt' && side.provider === 'deepgram'
                ? '{\n  "utterance_end_ms": "1600",\n  "keywords": "HiLux, Ranger"\n}'
                : '{\n  "utterance_end_ms": "1600"\n}'}
              value={side.advancedJson ?? ''}
              onChange={(e) => setSide({ ...side, advancedJson: e.target.value })}
            />
          </Field>
        );
      })()}
    </div>
  );
}

/* ───────────────────────────── engine toggle ───────────────────────────── */
/* STANDARD = the custom voice loop (STT/TTS providers below).
   ADVANCED = a managed low-latency pipeline run on the dealership's own
   Retell account; the vendor name stays out of the customer-facing copy. */

function EngineCard({ engine, setEngine, agentCount }) {
  const advanced = engine.mode === 'ADVANCED';

  const toggleBtn = (active) => ({
    flex: 1,
    padding: '10px 14px',
    fontSize: 12,
    fontFamily: 'var(--font-mono)',
    cursor: 'pointer',
    borderRadius: 'var(--radius-sm)',
    border: active ? '1px solid var(--accent)' : '1px solid var(--border-color)',
    background: active ? 'var(--accent-soft, rgba(99,102,241,.12))' : 'var(--bg-glass)',
    color: active ? 'var(--accent)' : 'var(--text-secondary)',
    fontWeight: active ? 600 : 400,
  });

  return (
    <div className="card animate-in" style={{ padding: 16 }}>
      <div className="card-label" style={{ marginBottom: 4 }}>VOICE ENGINE MODE</div>
      <div className="mono text-xs text-muted" style={{ marginBottom: 12 }}>
        Which pipeline carries this dealership’s AI voice calls and browser mic tests.
      </div>
      <div style={{ display: 'flex', gap: 10, marginBottom: advanced ? 14 : 0 }}>
        <button type="button" style={toggleBtn(!advanced)}
                onClick={() => setEngine((e) => ({ ...e, mode: 'STANDARD' }))}>
          Standard Engine
          <div className="text-xs" style={{ fontWeight: 400, marginTop: 2, opacity: .8 }}>
            Custom pipeline — your STT / TTS providers below
          </div>
        </button>
        <button type="button" style={toggleBtn(advanced)}
                onClick={() => setEngine((e) => ({ ...e, mode: 'ADVANCED' }))}>
          Advanced Engine
          <div className="text-xs" style={{ fontWeight: 400, marginTop: 2, opacity: .8 }}>
            Managed low-latency pipeline — bring your own account
          </div>
        </button>
      </div>

      {/* Agent credentials live in the nested VoiceAgentsPanel below (backend
          V42 stores one row per agent so a dealership can run several at
          once) rather than duplicated into this card's own form — asking for
          the agent id and API key in both places meant two copies of the same
          credential in two tables, and an operator who filled in only one got
          working inbound calls and silently non-working outbound ones. This
          card owns only the engine-mode switch and a one-line summary. */}
      {advanced && (
        <div style={{
          padding: '10px 12px', marginTop: 4,
          border: '1px solid var(--border-color)', borderRadius: 'var(--radius-sm)',
          background: 'var(--bg-glass)',
        }}>
          <div className="mono text-xs text-muted">
            {agentCount > 0
              ? `${agentCount} agent${agentCount === 1 ? '' : 's'} configured. `
                + 'Outbound calls and the mic test use the first active one.'
              : 'No agents configured yet — add one below to enable Advanced-engine calls.'}
          </div>
        </div>
      )}
    </div>
  );
}

/* ─────────────────────────────── the panel ─────────────────────────────── */

export default function VoiceProvidersPanel() {
  const [saved, setSaved] = useState(null);          // last GET result (masked)
  const [status, setStatus] = useState('NOT_CONFIGURED');
  const [stt, setStt] = useState(emptySide('deepgram'));
  const [tts, setTts] = useState(emptySide('elevenlabs'));
  // Engine mode only. Agent credentials live in Settings → Voice agents.
  const [engine, setEngine] = useState({ mode: 'STANDARD' });
  const [agentCount, setAgentCount] = useState(0);
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState(null);        // { kind: 'success'|'danger', text }
  const [testResult, setTestResult] = useState(null);

  const load = useCallback(async () => {
    try {
      const view = await voiceProvidersService.get();
      if (view) {
        setSaved(view);
        setStatus(view.status || 'PENDING');
        setStt((s) => ({
          ...s,
          provider: view.stt?.provider || 'deepgram',
          model: view.stt?.model || '',
          language: view.stt?.language || '',
          region: view.stt?.region || '',
          endpointingMs: view.stt?.endpointingMs ?? null,
        }));
        setTts((s) => ({
          ...s,
          provider: view.tts?.provider || 'elevenlabs',
          model: view.tts?.model || '',
          voiceId: view.tts?.voiceId || '',
          language: view.tts?.language || '',
          region: view.tts?.region || '',
        }));
        setEngine({ mode: view.engine?.mode || 'STANDARD' });
      }
    } catch {
      setNotice({ kind: 'danger', text: 'Could not load voice provider settings.' });
    }

    // How many agents exist, so the Advanced card can say whether the mode is
    // actually usable. Failure is not worth an error banner here — the count is
    // informational and the Voice agents tab is authoritative.
    try {
      const agents = await voiceAgentsService.list();
      setAgentCount(Array.isArray(agents) ? agents.filter((a) => a.isActive !== false).length : 0);
    } catch {
      setAgentCount(0);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const buildSide = (side, isTts) => {
    const clears = side.clears || [];
    const options = {};
    let hasOptions = false;
    for (const [k, v] of Object.entries(side.options || {})) {
      if (v !== '' && v != null) { options[k] = v; hasOptions = true; }
    }
    // Merge free-form Advanced options (JSON) over the typed option fields.
    for (const [k, v] of Object.entries(parseAdvanced(side.advancedJson).options)) {
      if (v !== '' && v != null) { options[k] = v; hasOptions = true; }
    }
    // Pending clears ride along as empty strings — the backend merge
    // deletes exactly those keys and leaves everything else stored.
    for (const key of clears) {
      if (key.startsWith('options.')) {
        const k = key.slice(8);
        if (!(k in options)) { options[k] = ''; hasOptions = true; }
      }
    }
    const clearApiKey = clears.includes('apiKey') && !side.apiKey;
    return {
      provider: side.provider,
      model: side.model || null,
      ...(isTts
        ? { voiceId: side.voiceId || null }
        : { endpointingMs: side.endpointingMs ? (parseInt(side.endpointingMs, 10) || null) : null }),
      language: side.language || null,
      region: side.region || null,
      // null = keep stored secret; "" = delete it; value = replace it
      apiKey: clearApiKey ? '' : (side.apiKey == null || side.apiKey === '' ? null : side.apiKey),
      options: hasOptions ? options : null,
    };
  };

  const onSave = async () => {
    // Block on malformed Advanced-options JSON rather than silently dropping it.
    const sttAdv = parseAdvanced(stt.advancedJson);
    const ttsAdv = parseAdvanced(tts.advancedJson);
    if (sttAdv.error || ttsAdv.error) {
      setNotice({ kind: 'danger', text: `Advanced options: ${sttAdv.error || ttsAdv.error}` });
      return;
    }
    setBusy(true); setNotice(null); setTestResult(null);
    try {
      const view = await voiceProvidersService.save({
        stt: buildSide(stt, false),
        tts: buildSide(tts, true),
        // Mode only. Sending the credential fields would write the deprecated
        // retell_* columns and re-create the duplication this panel just shed.
        engine: { mode: engine.mode },
      });
      setSaved(view);
      setStatus(view.status || 'PENDING');
      // secrets were consumed server-side — clear them from the form
      setStt((s) => ({ ...s, apiKey: null, options: {}, advancedJson: '', clears: [] }));
      setTts((s) => ({ ...s, apiKey: null, options: {}, advancedJson: '', clears: [] }));
      setNotice({ kind: 'success', text: 'Saved. Run "Test connection" to verify credentials.' });
    } catch (e) {
      setNotice({ kind: 'danger', text: e?.response?.data?.message || 'Save failed.' });
    } finally {
      setBusy(false);
    }
  };

  const onTest = async () => {
    setBusy(true); setNotice(null);
    try {
      const result = await voiceProvidersService.test();
      setTestResult(result);
      await load();
    } catch (e) {
      setNotice({ kind: 'danger', text: e?.response?.data?.message || 'Test failed.' });
    } finally {
      setBusy(false);
    }
  };

  // Tool-key rotation moved to Settings → Voice agents, where it belongs: keys
  // are per-agent now, so rotating "the" key is no longer a meaningful action
  // at the tenant level.

  const advancedMode = engine.mode === 'ADVANCED';

  return (
    <div className="flex-col" style={{ gap: 18 }}>
      <div className="card animate-in">
        <div className="flex-between" style={{ marginBottom: 4 }}>
          <div>
            <div style={{ fontSize: 16, fontWeight: 600 }}>Voice agents</div>
            <div className="mono text-xs text-muted" style={{ marginTop: 4 }}>
              Speech-to-text + text-to-speech for the AI voice agent. Changes apply to new calls within a minute — no restart.
            </div>
          </div>
          <StatusPill status={status} />
        </div>
        {saved?.lastError && (
          <div className="mono text-xs text-danger" style={{ marginTop: 8 }}>{saved.lastError}</div>
        )}
        {saved?.updatedAt && (
          <div className="mono text-xs text-muted" style={{ marginTop: 8 }}>
            Last updated {formatDateTime(saved.updatedAt)}{saved.updatedBy ? ` by ${saved.updatedBy}` : ''}
            {saved.lastVerifiedAt ? ` · verified ${formatDateTime(saved.lastVerifiedAt)}` : ''}
          </div>
        )}
      </div>

      <EngineCard engine={engine} setEngine={setEngine} agentCount={agentCount} />

      {/* Agent management lives here now, not on its own settings tab — it
          only matters once Advanced is selected, so nest it where that
          decision was just made rather than sending the operator elsewhere.
          `onAgentsChange` keeps this panel's own count (EngineCard's summary
          line, mic-test gating) in sync with every create/toggle/rotate/
          remove the nested panel does. */}
      {advancedMode && (
        <div className="card animate-in" style={{ padding: 16 }}>
          <VoiceAgentsPanel
            onAgentsChange={(agents) => setAgentCount(
              Array.isArray(agents) ? agents.filter((a) => a.isActive !== false).length : 0,
            )}
          />
        </div>
      )}

      {/* The STT/TTS provider forms only apply to the Standard engine — the
          Advanced engine manages speech itself. */}
      {!advancedMode && (
        <div style={{ display: 'flex', gap: 18, flexWrap: 'wrap' }} className="animate-in">
          <ProviderForm kind="stt" catalogue={STT_PROVIDERS} side={stt} setSide={setStt} saved={saved} />
          <ProviderForm kind="tts" catalogue={TTS_PROVIDERS} side={tts} setSide={setTts} saved={saved} />
        </div>
      )}

      {/* The browser mic test lives in Agent test console → Voice. */}

      {testResult && (
        <div className="card animate-in" style={{ padding: 14 }}>
          <div className="card-label" style={{ marginBottom: 8 }}>TEST RESULT</div>
          {testResult.engineOk != null ? (
            <div className="flex-row" style={{ gap: 8 }}>
              <span className={`pill ${testResult.engineOk ? 'success' : 'danger'} filled`}>ENGINE</span>
              <span className="mono text-xs">{testResult.engineMessage}</span>
            </div>
          ) : (
            <>
              <div className="flex-row" style={{ gap: 8, marginBottom: 4 }}>
                <span className={`pill ${testResult.sttOk ? 'success' : 'danger'} filled`}>STT</span>
                <span className="mono text-xs">{testResult.sttMessage}</span>
              </div>
              <div className="flex-row" style={{ gap: 8 }}>
                <span className={`pill ${testResult.ttsOk ? 'success' : 'danger'} filled`}>TTS</span>
                <span className="mono text-xs">{testResult.ttsMessage}</span>
              </div>
            </>
          )}
        </div>
      )}

      {notice && (
        <div className={`mono text-xs text-${notice.kind}`}>{notice.text}</div>
      )}

      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
        <button className="btn small" disabled={busy || !saved} onClick={onTest}>Test connection</button>
        <button className="btn small primary" disabled={busy} onClick={onSave}>Save changes</button>
      </div>

      <div className="mono text-xs text-muted" style={{ lineHeight: 1.6 }}>
        Keys are encrypted at rest (AES-256-GCM) and never returned by the API. Use each field’s
        “Clear” button to delete a stored secret — e.g. remove a Google API key and paste a
        service-account JSON instead. Setup steps are in Help → “Configure voice providers (STT & TTS)”.
      </div>
    </div>
  );
}
